import React, { Component, FormEvent } from 'react';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { TransactionJsonModel, TransactionVariableJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import CollectionNames from '../../../constant/CollectionNames';
import CustomLoading from '../../custom/CustomLoading';
import { RootState } from '../../../redux/store';
import { ActionState } from '../../../redux/reducers/ActionReducer';
import { changeAction, clearAction } from '../../../redux/actions/ActionActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import FormicaLabel from '../../custom/FormicaLabel';
import LanguageUtil from '../../../util/LanguageUtil';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import RoleHelper from '../../../RoleHelper';
import FormicaInput from '../../custom/FormicaInput';
import { SendTransactionActionJsonModel } from '../../../domain/collection/rule/action/ActionDomains';
import QueryBuilder = query.QueryBuilder;
import ComponentName from '../../../constant/ComponentName';

class ActionSendTransactionEditorComponent extends Component<Props, State> {
  public state = new State();

  private transactionSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const {
      action: { action },
      component: { module },
      user,
    } = this.props;
    const { loading } = this.state;
    const isKyc = module.includes('kyc');

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className={'grid grid-cols-5 w-full !max-h-[calc(100vh-15rem)] overflow-y-scroll pb-8'}>
        <div className={'col-span-1 flex flex-col border-r border-f-black-5 px-5 py-5 gap-y-5'}>
          <div className={'flex flex-col gap-y-3'}>
            <span className={'text-primary font-bold text-md'}>
              {LanguageUtil.get({ id: 'pages.action.transaction.title', defaultMessage: 'INTEGRATION CONFIGURATION' })}
            </span>
            <span className={'flex bg-[#F7F8FA] rounded-[4px] w-full justify-between gap-x-1 px-1 py-1 h-[36px]'}>
              <div
                onClick={() => this.onSelectType('transaction')}
                className={`${action.type == 'transaction' ? 'bg-white opacity-100' : 'opacity-50'} w-full cursor-pointer text-f-black-1 px-1 py-1 rounded-[2px] flex items-center justify-center`}
              >
                {LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Transaction' })}
              </div>
              {isKyc && user.activeRiskType == 'compliance' && (
                <div
                  onClick={() => this.onSelectType('kyc')}
                  className={`${action.type == 'kyc' ? 'bg-white opacity-100' : 'opacity-50'} w-full cursor-pointer text-f-black-1 px-1 py-1 rounded-[2px] flex items-center justify-center`}
                >
                  {LanguageUtil.get({ id: 'common.label.kyc', defaultMessage: 'KYC' })}
                </div>
              )}
            </span>
            <div className={'flex flex-col w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })} />
              <FormicaSearchSelectBox
                options={this.getTransactionOptions()}
                rounded
                placeholder={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })}
                disabled={!RoleHelper.canEdit('Action')}
                value={action.transaction}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeTransaction(e)}
                data-testid={`${ComponentName.ACTION}_channel_select`}
              />
            </div>
            <div className={'flex flex-col w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'transaction' })} />
              <FormicaSearchSelectBox
                options={this.getTransactionOptions()}
                rounded
                placeholder={LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Transaction' })}
                disabled={!RoleHelper.canEdit('Action')}
                value={(action as SendTransactionActionJsonModel).variables.channel}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeChannel(e)}
                data-testid={`${ComponentName.ACTION}_transaction_select`}
              />
            </div>
            <div className={'flex flex-col w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.type', defaultMessage: 'Type' })} />
              <FormicaSearchSelectBox
                options={this.getRequestTypeOptions()}
                rounded
                placeholder={LanguageUtil.get({ id: 'common.label.type', defaultMessage: 'Type' })}
                disabled={!RoleHelper.canEdit('Action')}
                value={(action as SendTransactionActionJsonModel).variables.type}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeType(e)}
                data-testid={`${ComponentName.ACTION}_type_select`}
              />
            </div>
            <div className={'flex flex-col w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.timeout', defaultMessage: 'Timeout (ms)' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                type={'number'}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeTimeout(e)}
                value={action.timeout}
                placeholder={LanguageUtil.get({ id: 'common.label.timeout', defaultMessage: 'Timeout(ms)' })}
                data-testid={`${ComponentName.ACTION}_timeout_input`}
              />
            </div>
          </div>
        </div>
        <div className={'col-span-4 flex flex-col w-full h-full px-5 py-5 gap-y-2'}>
          <span className={'font-bold text-md text-primary'}>
            {LanguageUtil.get({ id: 'pages.action.transaction.transaction', defaultMessage: 'TRANSACTION INFORMATION' })}
          </span>
          {this.getRequest()}
        </div>
      </div>
    );
  }

  public componentDidMount() {
    const {
      action: { action },
      changeAction,
    } = this.props;
    if ((action as SendTransactionActionJsonModel).variables == undefined) {
      (action as SendTransactionActionJsonModel).variables = {
        channel: '',
        type: 'SUBMIT',
        request: {
          key: '',
          id: '',
          variables: {},
          name: '',
        },
      };
      changeAction(action);
    }
    this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private startTracker = () => {
    const transactionQuery = QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription]);
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    this.computation?.stop();
  };

  private tracker = async () => {
    if (this.transactionSubscription?.ready()) {
      await this.buildData();
    }
  };

  private buildData = async () => {
    const transactionQuery = QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
    const transactions: TransactionJsonModel[] = await this.repository.findMany(transactionQuery);
    this.setState({ transactions, loading: false });
  };

  private getTransactionOptions() {
    const {
      action: { action },
    } = this.props;
    if (action.type == 'kyc') {
      return [
        {
          label: LanguageUtil.get({ id: 'common.label.individual', defaultMessage: 'Individual' }),
          value: 'individual',
        },
        {
          label: LanguageUtil.get({ id: 'common.label.entity', defaultMessage: 'Entity' }),
          value: 'entity',
        },
      ];
    } else {
      return this.state.transactions.map((transaction) => {
        return {
          label: transaction.name,
          value: transaction.name,
        };
      });
    }
  }
  private getRequestTypeOptions() {
    return ['SUBMIT', 'RESUBMIT', 'STORE', 'UPDATE', 'DELETE'].map((type) => {
      return {
        label: type,
        value: type,
      };
    });
  }

  private onChangeTransaction(e: FormicaSelectBoxItem) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.transaction = e.value;
    changeAction(action);
  }

  private onChangeChannel(e: FormicaSelectBoxItem) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as SendTransactionActionJsonModel).variables.channel = e.value;
    changeAction(action);
  }

  private onChangeType(e: FormicaSelectBoxItem) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as SendTransactionActionJsonModel).variables.type = e.value;
    switch (e.value) {
      case 'DELETE': {
        (action as SendTransactionActionJsonModel).variables.request.variables = {};
        break;
      }
    }
    changeAction(action);
  }

  private getChannelFieldInput(value: any, variable: TransactionVariableJsonModel) {
    return (
      <FormicaInput
        placeholder={variable.name}
        value={value}
        disabled={!RoleHelper.canEdit('Action')}
        onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeChannelField(e.currentTarget.value, variable)}
        data-testid={`${ComponentName.ACTION}_channel_field_${variable.name}_input`}
      />
    );
  }

  private onChangeChannelField(e: any, variable: TransactionVariableJsonModel) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as SendTransactionActionJsonModel).variables.request.variables[variable.name] = e;
    changeAction(action);
    /*
    switch (variable.properties.dataType) {
      case DataType.DATE:
        (action as SendTransactionActionJsonModel).request.variables[variable.name] = e instanceof Date ? e.valueOf() : typeof e == "string" ? new Date(e).valueOf() : e;
        break;
      case DataType.NUMBER: {
        try {
          if (e.length == 0) {
            (action as SendTransactionActionJsonModel).request.variables[variable.name] = null;
            break;
          }
          const nmb = Number(e);
          if (!isNaN(nmb)) {
            (action as SendTransactionActionJsonModel).request.variables[variable.name] = nmb
          } else {
            (action as SendTransactionActionJsonModel).request.variables[variable.name] = null
          }
        } catch (e) {
        }
        break;
      }
      default:
        (action as SendTransactionActionJsonModel).request.variables[variable.name] = e;
    }
     */
  }

  private getChannelFields = () => {
    const {
      action: { action },
    } = this.props;
    const { transactions } = this.state;
    const channel = (action as SendTransactionActionJsonModel).variables.channel;
    if (channel == undefined || channel.trim().length == 0) return;
    const transaction = transactions.find((transaction) => transaction.name == channel);
    if (transaction == undefined) return;
    return transaction.variables.map((variable: TransactionVariableJsonModel) => (
      <div className={'flex flex-col gap-y-1'}>
        <FormicaLabel label={variable.name} />
        {this.getChannelFieldInput((action as SendTransactionActionJsonModel).variables.request.variables[variable.name], variable)}
      </div>
    ));
  };

  private onChangeKey = (e: React.FormEvent<HTMLInputElement>) => {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as SendTransactionActionJsonModel).variables.request.key = e.currentTarget.value;
    changeAction(action);
  };
  private onChangeId = (e: React.FormEvent<HTMLInputElement>) => {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as SendTransactionActionJsonModel).variables.request.id = e.currentTarget.value;
    changeAction(action);
  };

  private onChangeTimeout = (e: any) => {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.timeout = parseInt(e.currentTarget.value);
    changeAction(action);
  };

  private getRequest = () => {
    const {
      action: { action },
    } = this.props;
    switch ((action as SendTransactionActionJsonModel).variables.type) {
      case 'SUBMIT':
      case 'STORE':
        return (
          <>
            <div className={'flex flex-col gap-y-1'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.key', defaultMessage: 'Key' })} />
              <FormicaInput
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.key', defaultMessage: 'Key' })}
                value={(action as SendTransactionActionJsonModel).variables.request.key}
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeKey(e)}
                data-testid={`${ComponentName.ACTION}_key_input`}
              />
            </div>

            {this.getChannelFields()}
          </>
        );
      case 'RESUBMIT':
      case 'UPDATE':
        return (
          <>
            <div className={'flex flex-col gap-y-1'}>
              <FormicaLabel label={'Id'} />
              <FormicaInput
                placeholder={'Id'}
                value={(action as SendTransactionActionJsonModel).variables.request.id}
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeId(e)}
                data-testid={`${ComponentName.ACTION}_id_input`}
              />
            </div>
            {this.getChannelFields()}
          </>
        );
      case 'DELETE':
        return (
          <div className={'flex flex-col gap-y-1'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.id', defaultMessage: 'Id' })} />
            <FormicaInput
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.id', defaultMessage: 'Id' })}
              value={(action as SendTransactionActionJsonModel).variables.request.id}
              disabled={!RoleHelper.canEdit('Action')}
              onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeId(e)}
              data-testid={`${ComponentName.ACTION}_id_input`}
            />
          </div>
        );
    }
  };

  private onSelectType(type: string) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.type = type;
    changeAction(action);
  }
}

interface Props extends ReduxProps, FormicaProps {}

class State {
  transactions: TransactionJsonModel[] = [];
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  action: state.action as ActionState,
  isUpdate: state.action.isUpdate,
  lastCategory: state.page.lastCategory,
  component: state.component,
  user: state.auth.user,
});
const mapDispatchToProps = {
  changeAction,
  clearAction,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(ActionSendTransactionEditorComponent));
